import { isFailedAnswer } from './findings';
import {
  isQuestionVisible,
  type AnswerMap,
  type Question,
  type TemplateDefinition,
} from './template';

/**
 * Attachment policy rules (spec §6 + owner-approved photo-required-on-fail).
 * Pure — unit tested in attachmentRules.test.ts.
 */

export type AttachmentPolicy = 'none' | 'optional' | 'required' | 'required_on_fail';
export type AttachmentKind = 'photo' | 'pdf';

/** Photos compressed client-side to stay at or under this (spec §8). */
export const MAX_PHOTO_BYTES = 500 * 1024;
/** PDFs are accepted up to this cap, rejected client-side above it (spec §8). */
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

/** Default: any question can take an optional attachment. */
export function attachmentPolicy(q: Question): AttachmentPolicy {
  return q.attachments ?? 'optional';
}

/**
 * Which kinds this question accepts. Default BOTH (owner ruling
 * 2026-08-12): some items are proven by a document, not a picture.
 */
export function allowedKinds(q: Question): AttachmentKind[] {
  const t = q.attachmentTypes ?? 'both';
  return t === 'both' ? ['photo', 'pdf'] : [t];
}

/**
 * Is an attachment mandatory for this question right now?
 * 'required' = always (once the question applies); 'required_on_fail' =
 * only when the scored answer is a failure.
 */
export function isAttachmentRequired(
  q: Question,
  value: AnswerMap[string],
): boolean {
  const policy = attachmentPolicy(q);
  if (policy === 'required') return true;
  if (policy === 'required_on_fail') return isFailedAnswer(q, value);
  return false;
}

/** Minimal shape the rules need — LocalAttachment satisfies it. */
export interface AttachmentRef {
  questionId: string;
}

/**
 * Visible questions whose attachment requirement is unmet. Blocks
 * submission; hidden (gated-away) questions never block.
 */
export function missingRequiredAttachments(
  definition: TemplateDefinition,
  answers: AnswerMap,
  attachments: readonly AttachmentRef[],
): Question[] {
  const byQuestion = new Set(attachments.map((a) => a.questionId));
  const missing: Question[] = [];
  for (const section of definition.sections) {
    for (const q of section.questions) {
      if (!isQuestionVisible(definition, section, q, answers)) continue;
      if (isAttachmentRequired(q, answers[q.id]) && !byQuestion.has(q.id)) {
        missing.push(q);
      }
    }
  }
  return missing;
}
